import { Grid } from "@material-ui/core";
import { toastr } from "react-redux-toastr";
import { useAuth0 } from "@auth0/auth0-react";
import MemberService from "Lib/API/Member/index";
import React, { Fragment, useState, useEffect, useRef } from "react";
// components
import AlertDialog from "components/Dialog/index";
import { hasAccessToModule } from "utils/functions";
import NotAuthorized from "components/NotAuthorized/index";
// direct import
import Feeds from "./view/feed";
import Events from "./view/events";
import SideDrawer from "./view/profile";
import NewTask from "./view/new-tasks/index";
import EventDrawer from "./view/event-listing";

const EasyCarePage = ({ account }) => {
  const feedsRef = useRef(null);
  const { getAccessTokenSilently } = useAuth0();
  const canAccessPastoral = hasAccessToModule("pastoral");
  // state
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [totalFeeds, setTotalFeeds] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refetchEvent, setRefetchEvent] = useState(0);
  const [feedCardData, setFeedCardData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeClassPage, setActiveClassPage] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState({
    eventType: [],
    name: [],
    archivedType: ["no"],
    dateType: [],
  });

  useEffect(() => {
    if (canAccessPastoral) {
      if (feedsRef?.current) {
        feedsRef.current.scrollTop = 0;
      }
      setFeedCardData([]);
      setPage(1);
    }
  }, [canAccessPastoral]);

  const fetchMyAPI = async (initialPage) => {
    setLoading(true);
    const currentPage = initialPage || page;
    const token = await getAccessTokenSilently();
    MemberService.getMembersFeeds({
      token,
      page: currentPage,
      limit: 30,
      accountId: account?._id,
    })
      .then((res) => {
        if (res) {
          let newFeedList = currentPage === 1 ? [] : [...feedCardData];
          newFeedList.push(...res?.feed);
          setFeedCardData(newFeedList);
          setTotalFeeds(res?.hasMore);
          setPage((prevPage) => prevPage + 1);
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  let hasFetchedEasyCare = false;

  useEffect(() => {
    let timeoutId;

    if (!hasFetchedEasyCare && canAccessPastoral) {
      timeoutId = setTimeout(() => {
        hasFetchedEasyCare = true;
        fetchMyAPI(1);
      }, 50); // 50ms delay prevents rapid  fire
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [canAccessPastoral]);

  const onEventChange = (key, value, options = []) => {
    setActiveClassPage(0);
    setSelectedFilter((prev) => {
      const currentArray = [...(prev[key] || [])];
      const allValues = options?.map((opt) => opt.value);

      let updatedArray;

      if (value === "all") {
        const isAllSelected = allValues.every((val) =>
          currentArray.includes(val)
        );
        updatedArray = isAllSelected ? [] : [...allValues];
      } else {
        const isSelected = currentArray.includes(value);
        if (isSelected) {
          updatedArray = currentArray.filter((item) => item !== value);
        } else {
          updatedArray = [...currentArray, value];
        }
      }

      return {
        ...prev,
        [key]: updatedArray,
      };
    });
  };

  const onEventClick = (item) => {
    setSelectedData(item);
    setDrawerOpen(true);
  };
  const handleEvent = (item) => {
    setSelectedEvent(item);
    setEventOpen(true);
  };

  const handleEventFromEasyCare = (item) => {
    setSelectedEvent(item);
    setEventOpen(true);
  };
  const refetchCallback = () => {
    setPage(1);
    fetchMyAPI(1);
    setRefetchEvent(refetchEvent + 1);
  };

  const confirmCloseHandler = () => {
    setTaskOpen(false);
    setConfirmClose(false);
  };

  const taskCloseModal = () => setTaskOpen(false);
  const openNewTaskDrawer = () => setTaskOpen(true);
  const closeNewTaskModal = () => setConfirmClose(true);

  return (
    <Fragment>
      {!canAccessPastoral ? (
        <NotAuthorized />
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Feeds
                totalFeeds={totalFeeds}
                fetchMyAPI={fetchMyAPI}
                feedCardData={feedCardData}
                loading={loading}
                feedsRef={feedsRef}
                onEventClick={onEventClick}
                handleEvent={handleEvent}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Events
                accountId={account?._id}
                fetchMyAPI={fetchMyAPI}
                setPage={setPage}
                onEventChange={onEventChange}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                onRowClick={handleEvent}
                refetchEvent={refetchEvent}
                activeClassPage={activeClassPage}
                setActiveClassPage={setActiveClassPage}
              />
            </Grid>
          </Grid>
          {drawerOpen && (
            <SideDrawer
              open={drawerOpen}
              onClose={() => {
                setDrawerOpen(false);
                setSelectedData(null);
              }}
              account={account}
              selectedData={selectedData}
              handleEvent={handleEventFromEasyCare}
            />
          )}
          {eventOpen && (
            <EventDrawer
              open={eventOpen}
              onClose={() => {
                setEventOpen(false);
                setSelectedEvent(null);
              }}
              account={account}
              selectedData={{
                ...selectedEvent,
                eventId: selectedEvent?.eventId ?? selectedEvent?.eventID,
              }}
              openNewTaskDrawer={openNewTaskDrawer}
              eventOpenData={selectedData}
              refetchCallback={refetchCallback}
            />
          )}
          {taskOpen && (
            <NewTask
              closeNewTaskModal={closeNewTaskModal}
              taskOpen={taskOpen}
              taskCloseModal={taskCloseModal}
            />
          )}
        </>
      )}
      {confirmClose && (
        <AlertDialog
          open={confirmClose}
          onDecline={() => {
            setConfirmClose(false);
          }}
          onAccept={confirmCloseHandler}
          title={"Are you sure?"}
          description={"Do you want to discard the changes you made?"}
        />
      )}
    </Fragment>
  );
};
export default EasyCarePage;
